const toPositiveInteger = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const setIfPresent = (target, key, value) => {
  if (value !== undefined && value !== null && value !== "") target[key] = value;
};

export const buildMonitoringQuery = (filters = {}) => {
  const query = {};
  setIfPresent(query, "fechaDesde", filters.fechaDesde);
  setIfPresent(query, "fechaHasta", filters.fechaHasta);
  setIfPresent(query, "usuarioId", toPositiveInteger(filters.usuarioId));
  setIfPresent(query, "sessionId", toPositiveInteger(filters.sessionId));
  setIfPresent(query, "page", toPositiveInteger(filters.page));
  setIfPresent(query, "pageSize", toPositiveInteger(filters.pageSize));
  return query;
};

export const buildAdministrationQuery = (filters = {}) => {
  const query = {};
  setIfPresent(query, "fechaDesde", filters.fechaDesde);
  setIfPresent(query, "fechaHasta", filters.fechaHasta);
  setIfPresent(query, "estado", filters.status || filters.estado);
  setIfPresent(query, "calendarioKioscoGrupoId", toPositiveInteger(
    filters.groupId || filters.calendarioKioscoGrupoId
  ));
  setIfPresent(query, "calendarioId", toPositiveInteger(filters.calendarId));
  setIfPresent(query, "kioscoId", toPositiveInteger(filters.kioscoId));
  setIfPresent(query, "sedeId", toPositiveInteger(filters.sedeId));
  setIfPresent(query, "usuarioId", toPositiveInteger(filters.usuarioId));
  setIfPresent(query, "page", toPositiveInteger(filters.page));
  setIfPresent(query, "pageSize", toPositiveInteger(filters.pageSize));
  return query;
};

const positiveId = (value) => toPositiveInteger(value) || null;

const sessionStatus = (session = {}) => (
  session.estado || session.estadoOperativo || session.status
  || (session.ultimoIntento && session.ultimoIntento.resultado)
  || "scheduled"
);

export const flattenKioskGroups = (groups = []) => (
  (Array.isArray(groups) ? groups : []).reduce((rows, group = {}) => {
    const roster = Array.isArray(group.roster) ? group.roster : [];
    roster.forEach((session) => {
      const sessionId = positiveId(session.calendarioKioscoSesionId || session.sessionId);
      const usuarioId = positiveId(session.usuarioId);
      if (!sessionId || !usuarioId) return;
      rows.push({
        sessionId,
        usuarioId,
        nombre: session.nombre || "Sin nombre",
        status: sessionStatus(session),
        result: session.ultimoIntento && session.ultimoIntento.resultado,
        intentosTotal: Number(session.intentosTotal) || 0,
        fecha: group.fecha || null,
        inicioLocal: group.inicioLocal || null,
        finLocal: group.finLocal || null,
        inicioUtc: group.inicioUtc || null,
        finUtc: group.finUtc || null,
        grupoId: positiveId(group.grupoId),
        calendarioKioscoGrupoId: positiveId(group.calendarioKioscoGrupoId),
        calendarioKioscoId: positiveId(group.calendarioKioscoId),
        capacidad: Number(group.capacidad) || 0,
        ocupacion: Number(group.ocupacion) || 0,
        disponible: Number(group.disponible) || 0,
        groupStatus: group.estadoOperativo || null,
      });
    });
    return rows;
  }, [])
);

export const filterAdministrationRows = (rows = [], filters = {}) => {
  const selectedUserId = positiveId(filters.usuarioId);
  const selectedSessionId = positiveId(filters.sessionId);
  const selectedStatus = filters.status || filters.estado;

  return (Array.isArray(rows) ? rows : []).filter(row => {
    if (selectedUserId && row.usuarioId !== selectedUserId) return false;
    if (selectedSessionId && row.sessionId !== selectedSessionId) return false;
    if (selectedStatus && ![row.status, row.result, row.groupStatus].includes(selectedStatus)) return false;
    return true;
  });
};

const isCompleted = (row) => row.status === "completed" || row.result === "completed";
const isFailed = (row) => (
  ["failed", "no_show", "cancelled", "rescheduled", "absent", "incomplete", "retry_required"]
    .includes(row.status) ||
  ["failed", "absent", "incomplete", "retry_required"].includes(row.result)
);

const groupKey = (value = {}) => (
  value.calendarioKioscoGrupoId
    || `${value.calendarioKioscoId || ""}:${value.grupoId || ""}:${value.fecha || ""}`
);

export const buildAdministrationSummary = (groups = [], visibleRows) => {
  const hasVisibleRows = Array.isArray(visibleRows);
  const rows = hasVisibleRows ? visibleRows : flattenKioskGroups(groups);
  const sourceGroups = Array.isArray(groups) ? groups : [];
  const visibleGroupKeys = new Set(rows.map(row => groupKey(row)));
  const visibleGroups = hasVisibleRows
    ? sourceGroups.filter(group => visibleGroupKeys.has(groupKey(group)))
    : sourceGroups;
  const completedSessions = rows.filter(isCompleted).length;
  const failedSessions = rows.filter(isFailed).length;
  const scheduledSessions = rows.length;
  const uniqueEmployees = new Set(rows.map(row => row.usuarioId));
  const capacity = visibleGroups
    .reduce((sum, group) => sum + (Number(group && group.capacidad) || 0), 0);
  const occupied = visibleGroups
    .reduce((sum, group) => sum + (Number(group && group.ocupacion) || 0), 0);

  return {
    scheduledSessions,
    completedSessions,
    pendingSessions: Math.max(scheduledSessions - completedSessions - failedSessions, 0),
    failedSessions,
    capacity,
    occupied,
    available: Math.max(capacity - occupied, 0),
    groups: visibleGroups.length,
    employees: uniqueEmployees.size,
  };
};

export const formatKioskMetric = (value) => {
  if (value === null || value === undefined || value === "") return "N/D";
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "N/D";
};

export const getKioskDfaStatus = (alpha1) => {
  if (alpha1 === null || alpha1 === undefined || alpha1 === "") return null;

  const numeric = Number(alpha1);
  if (!Number.isFinite(numeric)) return null;
  if (numeric < 0.75) return "good_for_load";
  if (numeric < 1) return "balanced";
  return "elevated_stress";
};

export const formatKioskDfaStatus = (value) => {
  const status = value && typeof value === "object" ? value.status : value;
  return {
    good_for_load: "Buen estado",
    balanced: "Equilibrado",
    elevated_stress: "Estrés elevado",
  }[status] || "N/D";
};

export const formatKioskTime = (value) => {
  if (value === null || value === undefined || value === "") return "N/D";

  let text;
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return "N/D";
    text = value.toISOString();
  } else {
    text = String(value);
  }

  const match = text.match(/(?:^|[T\s])(\d{2}):(\d{2})(?::\d{2})?/);
  return match ? `${match[1]}:${match[2]}` : "N/D";
};
